import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component } from "@angular/core";
import { Select, ofActionSuccessful } from "@ngxs/store";
import { TranslateModule } from "@ngx-translate/core";
import { Observable, combineLatest, filter, map, merge, take, takeUntil, withLatestFrom } from "rxjs";

import { ImportDataResultComponent } from "@app/pages/import-data-result/import-data-result.component";
import { PageBasedComponent } from "@app/pages/page.based.component";
import { LoadingSpinnerComponent } from "@app/shared/components/loading-spinner/loading-spinner.component";
import { MessageDataComponent } from "@app/shared/components/message-data/message-data.component";
import { DataGridComponent } from "@app/shared/components/smart-data-grid/data-grid/data-grid.component";
import { SmartSearchFormComponent } from "@app/shared/components/smart-search-form/smart-search-form.component";
import { SpinnerButtonComponent } from "@app/shared/components/spinner-button/spinner-button.component";
import { NgxsActionName, NgxsFormPath } from "@app/shared/constants";
import { KpiEconomy } from "@app/shared/classes/kpi-economy";
import { SearchByItem, WebApiResponse, WebApiResponseBase } from "@app/shared/classes/general";
import { AppDataState } from "@app/shared/stores/app-data.state";
import { ImportResult } from "@app/shared/classes/import-result";
import * as AppDataAction from "@app/shared/actions/app-data.action";
import { DataGridColumnDef, GetEconomySearchByList } from "./economy.constant";
import { CommonModalDialogComponent } from "@app/shared/components/common-modal-dialog/common-modal-dialog.component";
import { CommonModalDialogOption, ModalDialogButtonType, ModalDialogResult } from "@app/shared/components/common-modal-dialog/common-modal-dialog.constant";
import { EconomyEditModalDialogComponent } from "@app/shared/components/edit-modal-dialog/economy-edit-modal-dialog/economy-edit-modal-dialog.component";
import { CommonPageFilterSetting, CommonSearchCriteria, CreatePageFilterSettingWithLstChangeOnSort } from "@app/shared/models/common-data.model";
import { SiteConfigHelper } from "@app/shared/utils/site-config.helper";
import { ExcelImportButtonComponent } from "@app/shared/components/excel-import-button/excel-import-button.component";
import { CsvImportType } from "@app/shared/enums";
import { DisableDeleteButtonDirective } from "@app/shared/directive/disable-delete-button.directive";
import { DisableEditButtonDirective } from "@app/shared/directive/disable-edit-button.directive";

@Component({
  selector: "app-economy",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LoadingSpinnerComponent,
    DataGridComponent,
    MessageDataComponent,
    CommonModule,
    TranslateModule,
    DisableDeleteButtonDirective,
    DisableEditButtonDirective,
    SmartSearchFormComponent,
    SpinnerButtonComponent,
    ImportDataResultComponent,
    EconomyEditModalDialogComponent,
    ExcelImportButtonComponent,
  ],
  templateUrl: "./economy.component.html",
  styleUrl: "./economy.component.css",
})
export class EconomyComponent extends PageBasedComponent {
  dataGridColumnDef = DataGridColumnDef;
  formPath = NgxsFormPath.KPI_ECONOMY_SEARCH_FORM;
  selectedItems: KpiEconomy[] = [];

  dataSource$: Observable<KpiEconomy[]>;

  economySearchByList$: Observable<SearchByItem[]>;

  @Select(AppDataState.currentKpiEconomySearchBy) currentKpiEconomySearchBy$: Observable<CommonSearchCriteria[]>;
  @Select(AppDataState.currentPageFilterSetting(NgxsActionName.GET_KPI_ECONOMY)) currentPageFilterSetting$: Observable<CommonPageFilterSetting>;

  @Select(AppDataState.taskExecuting(NgxsActionName.GET_KPI_ECONOMY)) searching$: Observable<boolean>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_KPI_ECONOMY)) response$: Observable<WebApiResponse<KpiEconomy[]>>;
  @Select(AppDataState.taskError(NgxsActionName.GET_KPI_ECONOMY)) errorResponse$: Observable<WebApiResponseBase>;
  @Select(AppDataState.taskError(NgxsActionName.DELETE_KPI_ECONOMY)) deleteErrorResponse$: Observable<WebApiResponseBase>;

  @Select(AppDataState.taskExecuting(NgxsActionName.IMPORT_KPI_ECONOMY_DATA)) importing$: Observable<boolean>;
  @Select(AppDataState.taskResponse(NgxsActionName.IMPORT_KPI_ECONOMY_DATA)) importResponse$: Observable<ImportResult[]>;
  @Select(AppDataState.taskError(NgxsActionName.IMPORT_KPI_ECONOMY_DATA)) importErrorResponse$: Observable<WebApiResponseBase>;

  constructor() {
    super();
    this.functionName = NgxsActionName.GET_KPI_ECONOMY;
  }

  override ngOnInit() {
    super.ngOnInit();
    this.currentKpiEconomySearchBy$.pipe(take(1)).subscribe((searchCriteria) => this.defaultSearchCriteriaSub.next(searchCriteria));
    this.errorMessage$ = merge(this.commonDataLoadErrorMessage$, this.errorResponse$.pipe(map((errorResponse) => this.getErrorMessages([errorResponse]))));

    this.dataSource$ = this.response$.pipe(
      filter(Boolean),
      map((c) => c.data)
    );
    this.totalCount$ = this.response$.pipe(
      filter(Boolean),
      map((c) => c.totalCount)
    );

    var kpis$ = this.kpiAllData$.pipe(
      filter(Boolean),
      map((c) => c.data),
      filter(Boolean),
      map((kpi) => kpi.filter((item) => item.kpiCategory.kpiCategoryId === SiteConfigHelper.GetKpiCategoryIdMapping().EconomyKPI).flatMap((item) => item))
    );
    this.economySearchByList$ = combineLatest([this.currentTerminalDropDowns$, this.currentSubRegions$, kpis$, this.userCountry$]).pipe(map((c) => GetEconomySearchByList(c)));

    this.actions$
      .pipe(ofActionSuccessful(AppDataAction.DeleteKpiEconomy), withLatestFrom(this.deleteErrorResponse$), takeUntil(this.destroy$))
      .subscribe(([_, deleteErrorResponse]) => this.showResultDialog(deleteErrorResponse, "ml_143948", "ml_143949", this.onSearchWithExistedPageFilter));

    this.actions$
      .pipe(ofActionSuccessful(AppDataAction.ImportKpiEconomyData), withLatestFrom(this.importResponse$, this.importErrorResponse$, this.profileMe$), takeUntil(this.destroy$))
      .subscribe(([_, importResponse, importErrorResponse, profileMe]) =>
        this.showImportResult(importResponse as ImportResult[], importErrorResponse, "ml_143952", "ml_143950", profileMe, this.onSearchWithLastModifyPageFilter)
      );
  }

  rowSelected = (data: KpiEconomy[]) => {
    this.selectedItems = data ?? [];
  };

  override onSearch = () => {
    this.selectedItems = [];
    this.store.dispatch(new AppDataAction.GetKpiEconomy());
  };

  onSearchWithExistedPageFilter = () => {
    this.selectedItems = [];
    this.store.dispatch(new AppDataAction.GetKpiEconomy(null));
  };

  onSearchWithLastModifyPageFilter = () => {
    this.selectedItems = [];
    this.store.dispatch(new AppDataAction.GetKpiEconomy(CreatePageFilterSettingWithLstChangeOnSort(this.functionName)));
  };

  onGetData = (info: CommonPageFilterSetting) => {
    info.functionName = NgxsActionName.GET_KPI_ECONOMY;
    this.store.dispatch(new AppDataAction.GetKpiEconomy(info));
  };

  onEdit = () => {
    this.dialog.open(EconomyEditModalDialogComponent, {
      width: "500px",
      data: { data: this.selectedItems[0], successHandler: this.onSearchWithExistedPageFilter },
      disableClose: true,
    });
  };

  onDelete = () => {
    const dialogRef = this.dialog.open(CommonModalDialogComponent, {
      data: { header: true, message: "ml_143933", buttonType: ModalDialogButtonType.YES_NO } as CommonModalDialogOption,
      disableClose: false,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result.data !== ModalDialogResult.YES) {
          return;
        }

        this.store.dispatch(new AppDataAction.DeleteKpiEconomy(this.selectedItems.map((item) => item.kpiEconomy_Id)));
      });
  };

  onImport = (file: File) => {
    this.excelImportService.OnFileSlected(
      file,
      [],
      (fileData) => {
        var importData = this.csvService.GetImportObjectFromCsvData(CsvImportType.KPI_ECONOMY, fileData);
        if (importData.length === 0) {
          this.showImportEmptyDataResult();
        } else {
          this.store.dispatch(new AppDataAction.ImportKpiEconomyData(importData));
        }
      },
      this.showImportEmptyDataResult
    );
  };
}
